import { createReducer, on } from '@ngrx/store';

import { initialState, ChatState } from './chat.state';
import * as ChatActions from './chat.actions';

import { IConversationData } from '../../interfaces/conversations.interfaces';

import { EMessageContent } from '../../enums/conversation.enums';

const sortByLastMessage = (conversations: IConversationData[]): IConversationData[] => {
  return conversations.sort((a, b) => b.last_message_timestamp - a.last_message_timestamp);
};

const processLastMessage = (lastMessage: string, creatorName: string): string => {
  if (lastMessage?.includes(EMessageContent.GENERATING_IMAGE)) {
    return `${creatorName} shared a media`;
  }
  return lastMessage;
};

export const chatReducer = createReducer(
  initialState,

  // Load Conversations
  on(ChatActions.loadConversations, (state: ChatState) => ({
    ...state,
    loading: true,
    error: null
  })),

  on(ChatActions.loadConversationsSuccess, (state: ChatState, { conversations }) => ({
    ...state,
    conversations: conversations.map(conv => ({
      ...conv,
      last_message: processLastMessage(conv.last_message, conv.creator_name)
    })),
    loading: false,
    error: null
  })),

  on(ChatActions.loadConversationsFailure, (state: ChatState, { error }) => ({
    ...state,
    loading: false,
    error: error as string
  })),

  // Add Conversation
  on(ChatActions.addConversation, (state: ChatState) => ({
    ...state,
    loading: true,
    error: null
  })),

  on(ChatActions.addConversationSuccess, (state: ChatState, { conversation }) => {
    const processedConversation = {
      ...conversation,
      last_message: processLastMessage(conversation.last_message, conversation.creator_name)
    };

    const existingIndex = state.conversations.findIndex(
      conv => conv.conversation_id === conversation.conversation_id
    );

    let updatedConversations;
    if (existingIndex !== -1) {
      // Update existing conversation
      updatedConversations = [...state.conversations];
      updatedConversations[existingIndex] = {
        ...updatedConversations[existingIndex],
        ...processedConversation
      };
    } else {
      // Add new conversation
      updatedConversations = [processedConversation, ...state.conversations];
    }

    return {
      ...state,
      conversations: sortByLastMessage(updatedConversations),
      loading: false,
      error: null
    };
  }),

  on(ChatActions.addConversationFailure, (state: ChatState, { error }) => ({
    ...state,
    loading: false,
    error: error as string
  })),

  // Update Conversation
  on(ChatActions.updateConversation, (state: ChatState) => ({
    ...state,
    loading: true,
    error: null
  })),

  on(ChatActions.updateConversationSuccess, (state: ChatState, { conversation }) => {
    const updatedConversations = state.conversations.map(conv =>
      conv.conversation_id === conversation.conversation_id ? { ...conv, ...conversation } : conv
    );

    return {
      ...state,
      conversations: updatedConversations,
      loading: false,
      error: null
    };
  }),

  on(ChatActions.updateConversationFailure, (state: ChatState, { error }) => ({
    ...state,
    loading: false,
    error: error as string
  })),

  // Select Conversation
  on(ChatActions.selectConversation, (state: ChatState, { conversationId }) => ({
    ...state,
    selectedConversationId: conversationId,
    conversations: state.conversations.map(conv => ({
      ...conv,
      isSelected: conv.conversation_id === conversationId
    })),
    error: null
  })),

  // Update Last Message
  on(
    ChatActions.updateLastMessage,
    (state: ChatState, { conversationId, message, creatorName }) => {
      const timestamp = Date.now();
      const existingIndex = state.conversations.findIndex(
        c => c.conversation_id === conversationId
      );

      let updatedConversations;
      if (existingIndex !== -1) {
        // Update existing conversation
        const updatedConversation = {
          ...state.conversations[existingIndex],
          last_message: processLastMessage(
            message,
            creatorName || state.conversations[existingIndex].creator_name
          ),
          last_message_timestamp: timestamp,
          isTyping: false
        };

        updatedConversations = [...state.conversations];
        updatedConversations[existingIndex] = updatedConversation;
      } else {
        // Create new conversation with minimal required data
        const newConversation: IConversationData = {
          conversation_id: conversationId,
          creator_id: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          creator_name: creatorName || 'Creator',
          last_message: processLastMessage(message, creatorName || 'Creator'),
          last_message_timestamp: timestamp,
          thumbnail_image: '',
          isSelected: false,
          isTyping: false,
          messages: [],
          subscribed: false,
          chat_state: {
            isInitialized: false,
            lastConnected: Date.now(),
            isTyping: false,
            lastTypingTimestamp: 0
          }
        };
        updatedConversations = [newConversation, ...state.conversations];
      }

      return {
        ...state,
        conversations: sortByLastMessage(updatedConversations),
        error: null
      };
    }
  ),

  // Toggle Typing Indicator
  on(ChatActions.toggleTypingIndicator, (state: ChatState, { conversationId, isTyping }) => ({
    ...state,
    conversations: state.conversations.map(conv =>
      conv.conversation_id === conversationId ? { ...conv, isTyping } : conv
    ),
    error: null
  })),

  // Clear Conversations
  on(ChatActions.clearConversations, (state: ChatState) => ({
    ...state,
    conversations: [],
    selectedConversationId: null,
    error: null
  })),

  // Set Conversations
  on(ChatActions.setConversations, (state: ChatState, { conversations }) => {
    try {
      // Validate conversations array
      if (!Array.isArray(conversations)) {
        return state;
      }

      // Process conversations safely
      const processedConversations = conversations
        .map(conv => {
          if (!conv || typeof conv !== 'object') {
            return null;
          }

          return {
            ...conv,
            last_message: processLastMessage(conv.last_message || '', conv.creator_name || '')
          };
        })
        .filter(Boolean) as IConversationData[];

      return {
        ...state,
        conversations: processedConversations,
        error: null
      };
    } catch {
      return {
        ...state,
        error: 'Failed to set conversations'
      };
    }
  }),

  // Update Conversation Offset
  on(ChatActions.updateConversationOffset, (state: ChatState, { conversationId, offset }) => ({
    ...state,
    conversations: state.conversations.map(conv =>
      conv.conversation_id === conversationId ? { ...conv, offset } : conv
    ),
    error: null as string | null
  })),

  // Update Message by Timestamp
  on(
    ChatActions.updateMessageByTimestamp,
    (state: ChatState, { conversationId, message, timestamp }) => {
      const targetTimestamp = Number(timestamp);

      return {
        ...state,
        conversations: state.conversations.map(conv => {
          if (conv.conversation_id !== conversationId) {
            return conv;
          }

          // Find and replace the first message with exact timestamp match
          const messageIndex = conv.messages.findIndex(msg => {
            const messageTimestamp = Number(msg.timestamp);
            return messageTimestamp === targetTimestamp;
          });

          const updatedMessages = [...conv.messages];
          if (messageIndex !== -1) {
            // Replace only the first matching message
            updatedMessages[messageIndex] = { ...message };
          }

          const updates: Partial<IConversationData> = {
            messages: updatedMessages
          };

          // Check if the replaced message is now the last message
          if (messageIndex !== -1 && updatedMessages.length > 0) {
            const isLastMessage = messageIndex === updatedMessages.length - 1;

            if (isLastMessage) {
              if (message.category === 'medias') {
                updates.last_message = `${conv.creator_name} shared a media`;
              } else {
                updates.last_message = message.body || message.payload?.message || 'New message';
              }
              updates.last_message_timestamp = message.timestamp;
            }
          }

          return { ...conv, ...updates };
        }),
        error: null as string | null
      };
    }
  )
);
